import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { AppShell } from "@/components/layout/AppShell";
import { getFirebaseAuth, googleProvider, isFirebaseConfigured } from "@/lib/firebase";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in · PN-CivilServices" },
      {
        name: "description",
        content: "Sign in to bookmark files and report issues.",
      },
      { property: "og:title", content: "Sign in · PN-CivilServices" },
      {
        property: "og:description",
        content: "Access your bookmarks and personal ledger.",
      },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) {
    nav({ to: "/" });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!isFirebaseConfigured()) {
      setErr("Firebase not configured yet. Ask an admin.");
      return;
    }
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Auth unavailable");
      await signInWithEmailAndPassword(auth, email, password);
      nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign in failed");
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setErr(null);
    if (!isFirebaseConfigured() || !googleProvider) return;
    const auth = getFirebaseAuth();
    if (!auth) return;
    try {
      await signInWithPopup(auth, googleProvider);
      nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign in failed");
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-4 pt-16 pb-24">
        <div className="label-card">Sign in</div>
        <h1 className="mt-1 font-display text-3xl font-semibold">
          Welcome back to the ledger.
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-md border border-hairline bg-surface px-3 h-11 outline-none focus:ring-2 focus:ring-paprika"
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-md border border-hairline bg-surface px-3 h-11 outline-none focus:ring-2 focus:ring-paprika"
            />
          </Field>
          {err ? (
            <div className="text-sm text-danger">{err}</div>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-paprika py-2.5 text-sm font-semibold uppercase tracking-widest text-white disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-[10px] label-card">
          <div className="h-px flex-1 bg-hairline" /> or <div className="h-px flex-1 bg-hairline" />
        </div>
        <button
          onClick={google}
          className="w-full rounded-md border border-hairline bg-surface py-2.5 text-sm font-medium hover:bg-surface-alt"
        >
          Continue with Google
        </button>

        <div className="mt-8 text-sm text-ink-soft text-center">
          New here?{" "}
          <Link to="/signup" className="text-paprika font-semibold">
            Create an account
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="label-card mb-1.5">{label}</div>
      {children}
    </label>
  );
}
